import { PLANTING, HARVEST, BASE_LAYERS } from '../constants/mapLayerIds';
import { AgronomicActivityType } from '../requesters/agVizRequester/agVizQuery';

/**
 * Map DMB layer IDs to UAPI Ag-Viz layer names
 * DMB uses lowercase hyphenated IDs, UAPI uses UPPERCASE_SNAKE_CASE
 */
const DMB_TO_UAPI_LAYER_MAP: Record<string, string> = {
  // Planting layers
  [PLANTING.HYBRID]: 'HYBRID',
  [PLANTING.POPULATION]: 'ACTUAL_POPULATION_BY_SEED_COUNT',
  [PLANTING.TARGET_POPULATION]: 'TARGET_POPULATION_BY_SEED_COUNT',
  [PLANTING.APPLIED_DOWN_FORCE]: 'APPLIED_DF',
  [PLANTING.DOWN_FORCE]: 'DOWN_FORCE',
  [PLANTING.DATE]: 'PLANTING_DATE',
  [BASE_LAYERS.PLANTER_ELEVATION]: 'ELEVATION',
  [PLANTING.PLANTER_SPEED]: 'PLANTER_SPEED',
  [PLANTING.SINGULATION]: 'SINGULATION',
  [PLANTING.SPACING]: 'SPACING',
  [PLANTING.ORGANIC_MATTER]: 'ORGANIC_MATTER',
  [PLANTING.CEC]: 'CEC',
  [PLANTING.SOIL_TEMPERATURE]: 'SOIL_TEMPERATURE',
  [PLANTING.UNIFORM_FURROW]: 'UNIFORM_FURROW',
  [PLANTING.CLEAN_FURROW]: 'CLEAN_FURROW',
  [PLANTING.FURROW_MOISTURE]: 'FURROW_MOISTURE',
  
  // Harvest layers
  [HARVEST.YIELD]: 'YIELD',
  [HARVEST.MOISTURE]: 'MOISTURE',
  [HARVEST.DATE]: 'HARVEST_DATE',
  [HARVEST.COMBINE_SPEED]: 'COMBINE_SPEED',
  [BASE_LAYERS.COMBINE_ELEVATION]: 'COMBINE_ELEVATION',
};

/**
 * Reverse map: UAPI layer names to DMB layer IDs
 */
const UAPI_TO_DMB_LAYER_MAP: Record<string, string> = Object.entries(
  DMB_TO_UAPI_LAYER_MAP
).reduce((acc, [dmbId, uapiName]) => {
  acc[uapiName] = dmbId;
  return acc;
}, {} as Record<string, string>);

/**
 * Convert DMB layer ID to UAPI layer name
 * @param dmbLayerId - DMB layer ID (e.g., 'plantingdate')
 * @returns UAPI layer name (e.g., 'PLANTING_DATE') or undefined if not mapped
 */
export function dmbLayerIdToUapiLayerName(dmbLayerId: string): string | undefined {
  return DMB_TO_UAPI_LAYER_MAP[dmbLayerId];
}

/**
 * Convert UAPI layer name to DMB layer ID
 * @param uapiLayerName - UAPI layer name (e.g., 'PLANTING_DATE')
 * @returns DMB layer ID (e.g., 'plantingdate') or the original name if not mapped
 */
export function uapiLayerNameToDmbLayerId(uapiLayerName: string): string {
  return UAPI_TO_DMB_LAYER_MAP[uapiLayerName] || uapiLayerName;
}

/**
 * Get the activity type (PLANTING or HARVESTING) based on DMB layer ID
 * @param dmbLayerId - DMB layer ID
 * @returns AgronomicActivityType or undefined if not a planting/harvest layer
 */
export function getActivityTypeFromDmbLayerId(
  dmbLayerId: string
): AgronomicActivityType | undefined {
  const plantingLayers = Object.values(PLANTING);
  const harvestLayers = Object.values(HARVEST);

  // Check planting layers (including base layer PLANTER_ELEVATION)
  if (plantingLayers.includes(dmbLayerId) || dmbLayerId === BASE_LAYERS.PLANTER_ELEVATION) {
    return AgronomicActivityType.PLANTING;
  }

  // Check harvest layers (including base layer COMBINE_ELEVATION)
  if (harvestLayers.includes(dmbLayerId) || dmbLayerId === BASE_LAYERS.COMBINE_ELEVATION) {
    return AgronomicActivityType.HARVESTING;
  }

  return undefined;
}

/**
 * Get all DMB layer IDs for a given activity type
 * @param activityType - PLANTING or HARVESTING
 * @returns Array of DMB layer IDs that have UAPI mappings
 */
export function getDmbLayerIdsForActivity(
  activityType: AgronomicActivityType
): string[] {
  const layerIds = Object.keys(DMB_TO_UAPI_LAYER_MAP);
  
  return layerIds.filter((layerId) => {
    const activity = getActivityTypeFromDmbLayerId(layerId);
    return activity === activityType;
  });
}

/**
 * Check if a DMB layer ID is supported by Ag-Viz
 * @param dmbLayerId - DMB layer ID
 * @returns true if the layer is supported by Ag-Viz
 */
export function isDmbLayerSupportedByAgViz(dmbLayerId: string): boolean {
  return dmbLayerId in DMB_TO_UAPI_LAYER_MAP;
}
